import { AxiosResponse } from 'axios';
import { axios } from '@repository/RequestClient';
import { InterviewPracticeCreateRq } from '@domain/rq/pass/interview/InterviewPracticeCreateRq';
import { InterviewPracticeQuestionsRs } from '@domain/rs/pass/interview/practice/InterviewPracticeQuestionsRs';
import { QuestionAnswerResultRs } from '@domain/rs/pass/interview/practice/QuestionAnswerResultRs';
import { InterviewPracticeQuestionPolicyRs } from '@domain/rs/pass/interview/practice/InterviewPracticeQuestionPolicyRs';
import { InterviewPracticeInProgressRs } from '@domain/rs/pass/interview/practice/InterviewPracticeInProgressRs';
import { InterviewHistoryListRs } from '@domain/rs/pass/interview/history/InterviewHistoryListRs';
import { InterviewPracticeCreateRs } from '@domain/rs/pass/interview/practice/InterviewPracticeCreateRs';

const prefix = '/interview-practices';

export default interface InterviewPracticeRepo {
  readInterviewPracticeQuestions(interviewPracticeSn: number): Promise<AxiosResponse<InterviewPracticeQuestionsRs>>;

  savePractice(sn: number, seconds: number, file: Blob): Promise<AxiosResponse>;

  readQuestionAnswerResult(questionSn: number): Promise<AxiosResponse<QuestionAnswerResultRs>>;

  readPracticeVideo(practiceQuestionSn: number): Promise<AxiosResponse<Blob>>;

  readQuestionPolicy(): Promise<AxiosResponse<InterviewPracticeQuestionPolicyRs>>;

  checkInProgress(): Promise<AxiosResponse<InterviewPracticeInProgressRs>>;

  readInterviewHistoryList(): Promise<AxiosResponse<InterviewHistoryListRs>>;

  deleteInterviewPractice(interviewPracticeSn: number): Promise<AxiosResponse>;

  createInterviewPractice(questions: InterviewPracticeCreateRq): Promise<AxiosResponse<InterviewPracticeCreateRs>>;
}

export class RemoteInterviewPracticeRepo implements InterviewPracticeRepo {
  readInterviewPracticeQuestions(interviewPracticeSn: number) {
    return axios.get<InterviewPracticeQuestionsRs>(`${prefix}/${interviewPracticeSn}/questions`);
  }

  savePractice(sn: number, seconds: number, file: Blob) {
    const formData = new FormData();
    formData.append('file', file, 'file.webm');
    formData.append('seconds', seconds.toString());
    return axios.request({
      method: 'POST',
      url: `${prefix}/questions/${sn}/answer-videos`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  readQuestionAnswerResult(practiceQuestionSn: number) {
    return axios.get<QuestionAnswerResultRs>(`${prefix}/questions/${practiceQuestionSn}/results`);
  }

  readPracticeVideo(practiceQuestionSn: number) {
    return axios.get<Blob>(`${prefix}/questions/${practiceQuestionSn}/answer-videos`, {
      responseType: 'blob',
    });
  }

  readQuestionPolicy() {
    return axios.get<InterviewPracticeQuestionPolicyRs>(`${prefix}/policies`);
  }

  checkInProgress(): Promise<AxiosResponse<InterviewPracticeInProgressRs>> {
    return axios.get(`${prefix}/in-progress`);
  }

  readInterviewHistoryList(): Promise<AxiosResponse<InterviewHistoryListRs>> {
    return axios.get<InterviewHistoryListRs>(`${prefix}/results`);
  }

  deleteInterviewPractice(interviewPracticeSn: number): Promise<AxiosResponse> {
    return axios.delete(`${prefix}/${interviewPracticeSn}`);
  }

  createInterviewPractice(rq: InterviewPracticeCreateRq) {
    return axios.post<InterviewPracticeCreateRs>(`${prefix}`, rq);
  }
}
